import React from "react";
import { ControlLabel, FormControl, FormControlProps, FormGroup, InputGroup } from "react-bootstrap";
import classnames from "classnames";
import { omit, pick } from "lodash";
import { Field, WrappedFieldProps } from "redux-form";

import { generate } from "../../utils/random";

import { ErrorMessages } from "./components/ErrorMessages";
import { RequiredIndicator } from "./components/RequiredIndicator";
import { INPUT_GROUP_STYLES, INPUT_OMIT_STYLES } from "./utils/styles";

export type SelectProps = FormControlProps & {
    addons?: React.ReactNode;
    showErrors?: boolean;
    validationLabel?: string;
    isDark?: boolean;
    isHorizontal?: boolean;

    labelStyle?: Record<string, string>;
    errorStyle?: Record<string, string>;
    invalidStyle?: Record<string, string>;
    focusStyle?: Record<string, string>;
    inputStyle?: Record<string, string>;
    style?: Record<string, string>;

    errorParameters?: Record<string, string>;
    fieldError?: string;
};

export const Select = ({
    addons,
    input,
    label,
    meta: { active, touched, invalid, error },
    showErrors = true,
    required,
    width,
    validationLabel = label,
    isDark,
    isHorizontal,
    labelStyle,
    errorStyle,
    invalidStyle,
    focusStyle,
    inputStyle,
    style,
    errorParameters,
    fieldError,
    ...props
}: SelectProps & WrappedFieldProps) => {
    const isInvalid = (invalid || error) && touched;

    const currentFocusStyle = active ? focusStyle : {};
    const fieldStyle =
        isInvalid || fieldError
            ? { width, ...inputStyle, ...invalidStyle, ...currentFocusStyle }
            : { width, ...inputStyle, ...currentFocusStyle };

    return (
        <FormGroup
            controlId={generate()}
            className={classnames({
                "keep-color-on-error": showErrors,
                "dark-theme": isDark,
                "row": isHorizontal,
            })}
            validationState={invalid && touched ? "error" : null}
            bsSize="small"
            style={{ ...style }}>
            {label && (
                <div className={classnames({ "col-xs-3 col-sm-2 inline-horizontal-content": isHorizontal })}>
                    <ControlLabel style={{ ...labelStyle }} className={classnames({ "full-width": !width })}>
                        {label}

                        <RequiredIndicator required={required} />
                    </ControlLabel>
                </div>
            )}

            <div className={classnames({ "col-xs-9 col-sm-10 inline-horizontal-content": isHorizontal && label })}>
                <InputGroup className={active ? "input-group-focus" : ""} style={pick(fieldStyle, INPUT_GROUP_STYLES)}>
                    {addons && <InputGroup.Addon>{addons}</InputGroup.Addon>}

                    <span className="form-control form-select-wrapper">
                        <FormControl
                            {...input}
                            {...props}
                            required={required}
                            componentClass="select"
                            bsClass="form-select"
                            style={omit(fieldStyle, INPUT_OMIT_STYLES)}
                        />
                    </span>
                </InputGroup>

                {showErrors && (
                    <ErrorMessages
                        fieldLabel={validationLabel}
                        errors={fieldError ? fieldError : touched ? error : undefined}
                        style={{ ...errorStyle }}
                        parameters={errorParameters}
                    />
                )}
            </div>
        </FormGroup>
    );
};

type SelectFieldProps = any & SelectProps;

export const SelectField = (props: SelectFieldProps) => <Field component={Select} {...props} />;
